import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMealPlan } from '../../contexts/MealPlanContext';
import { useCookingStates } from '../../hooks/useCookingStates';
import {
  getProductImage,
  getLocalizedProductName,
  findMatchedProduct,
} from '../../utils/recipeIngredientMatching';
import { translateQty } from '../Recipes/RecipeContent';
import { getStepIngredients } from '../Recipes/utils/stepIngredients';
import { getMeasureHint, isWeightOrVolumeQty } from '../../utils/measureHints';

// Normalize cooking_instructions into a consistent { stepTitle, details } shape.
const parseSteps = (instructions) => {
  let parsed = instructions;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return [];
    }
  }
  const steps = (parsed && parsed.cooking_instructions) || [];
  return steps.map((step, index) => {
    let parsedStep = step;
    if (typeof step === 'string' && step.trim().startsWith('{')) {
      try {
        parsedStep = JSON.parse(step);
      } catch {
        parsedStep = { title: step };
      }
    }
    const stepTitle =
      (parsedStep && (parsedStep.step_title || parsedStep.title)) || `Step ${index + 1}`;
    const rawDetails = (parsedStep && parsedStep.details) || [];
    const details = Array.isArray(rawDetails)
      ? rawDetails.map((d) => (typeof d === 'string' ? d : d?.detail || '')).filter(Boolean)
      : [];
    return {
      raw: parsedStep,
      stepTitle: typeof stepTitle === 'string' ? stepTitle : JSON.stringify(stepTitle),
      details,
    };
  });
};

// Inject the swipe transition keyframes once.
const ensureCookAnimations = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById('cooking-mode-animations')) return;
  const style = document.createElement('style');
  style.id = 'cooking-mode-animations';
  style.textContent = `
    @keyframes cookActiveFromNext {
      0% { opacity: 0.72; transform: translate3d(0, 54px, 0) scale(0.88); }
      68% { opacity: 1; transform: translate3d(0, -3px, 0) scale(1.015); }
      100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
    }
    @keyframes cookActiveFromPrev {
      0% { opacity: 0.72; transform: translate3d(0, -54px, 0) scale(0.88); }
      68% { opacity: 1; transform: translate3d(0, 3px, 0) scale(1.015); }
      100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
    }
    @keyframes cookPrevFromActive {
      0% { opacity: 0.95; transform: translate3d(0, 96px, 0) scale(1.48); }
      100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
    }
    @keyframes cookPrevPeek {
      0% { opacity: 0; transform: translate3d(0, -18px, 0) scale(0.96); }
      100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
    }
    @keyframes cookNextFromActive {
      0% { opacity: 0.95; transform: translate3d(0, -96px, 0) scale(1.48); }
      100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
    }
    @keyframes cookNextPeek {
      0% { opacity: 0; transform: translate3d(0, 34px, 0) scale(0.96); }
      100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
    }
    @keyframes cookProductIn {
      0% { opacity: 0; transform: translate3d(0, 14px, 0) scale(0.98); }
      100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
    }
    @media (prefers-reduced-motion: reduce) {
      .cook-motion { animation: none !important; }
    }
  `;
  document.head.appendChild(style);
};

const CookingModePage = () => {
  const { recipeIndex } = useParams();
  const navigate = useNavigate();
  const { t: tRecipes } = useTranslation('recipes');
  const { t: tMeal } = useTranslation('mealplan');
  const { mealPlan, productList } = useMealPlan();
  const { completeCooking } = useCookingStates();

  const index = parseInt(recipeIndex, 10);
  const recipe = Array.isArray(mealPlan) ? mealPlan[index] : null;
  const recipeId = recipe ? recipe.id || recipe.title : null;
  const storageKey = recipeId ? `9meals_cook_step_${recipeId}` : null;

  const steps = useMemo(() => (recipe ? parseSteps(recipe.instructions) : []), [recipe]);

  // Flatten every instruction across all steps into a single ordered list so that
  // prev/next can cross step boundaries (the instruction after the last instruction
  // of step N is the first instruction of step N+1).
  const flat = useMemo(() => {
    const arr = [];
    steps.forEach((s, si) => {
      const instrs = s.details.length ? s.details : [s.stepTitle];
      instrs.forEach((text, ii) => arr.push({ stepIdx: si, instrIdx: ii, text }));
    });
    return arr;
  }, [steps]);

  useEffect(() => {
    ensureCookAnimations();
  }, []);

  // stepIdx = which step (cooking_instructions item); instrIdx = which instruction (detail) within that step.
  const [stepIdx, setStepIdx] = useState(0);
  const [instrIdx, setInstrIdx] = useState(0);
  const [direction, setDirection] = useState('next');
  const pageRef = useRef(null);
  const skipNextPositionPersistRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;
    if (!window.matchMedia('(max-width: 767px)').matches) return undefined;

    const root = pageRef.current;
    window.scrollTo(0, 0);
    const scrollY = 0;
    const previousBody = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      overscrollBehavior: document.body.style.overscrollBehavior,
    };
    const previousHtml = {
      overflow: document.documentElement.style.overflow,
      overscrollBehavior: document.documentElement.style.overscrollBehavior,
    };
    const preventTouchMove = (event) => event.preventDefault();

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overscrollBehavior = 'none';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.overscrollBehavior = 'none';
    root?.addEventListener('touchmove', preventTouchMove, { passive: false });

    return () => {
      root?.removeEventListener('touchmove', preventTouchMove);
      document.body.style.overflow = previousBody.overflow;
      document.body.style.position = previousBody.position;
      document.body.style.top = previousBody.top;
      document.body.style.left = previousBody.left;
      document.body.style.right = previousBody.right;
      document.body.style.width = previousBody.width;
      document.body.style.overscrollBehavior = previousBody.overscrollBehavior;
      document.documentElement.style.overflow = previousHtml.overflow;
      document.documentElement.style.overscrollBehavior = previousHtml.overscrollBehavior;
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Restore saved position on mount.
  useEffect(() => {
    if (!storageKey || steps.length === 0) return;
    skipNextPositionPersistRef.current = true;
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey));
      if (saved && Number.isInteger(saved.stepIdx) && saved.stepIdx >= 0 && saved.stepIdx < steps.length) {
        const len = steps[saved.stepIdx].details.length || 1;
        const ii = Number.isInteger(saved.instrIdx) ? Math.min(Math.max(saved.instrIdx, 0), len - 1) : 0;
        setStepIdx(saved.stepIdx);
        setInstrIdx(ii);
      } else {
        setStepIdx(0);
        setInstrIdx(0);
      }
    } catch {
      setStepIdx(0);
      setInstrIdx(0);
    }
  }, [storageKey, steps]);

  // Persist current position.
  useEffect(() => {
    if (!storageKey) return;
    if (skipNextPositionPersistRef.current) {
      skipNextPositionPersistRef.current = false;
      return;
    }
    localStorage.setItem(storageKey, JSON.stringify({ stepIdx, instrIdx }));
  }, [storageKey, stepIdx, instrIdx]);

  // Build ingredient → matched product map (same shape as RecipeDrawer).
  const ingredientProducts = useMemo(() => {
    if (!recipe?.ingredients || !productList?.length) return {};
    const map = {};
    for (const ing of recipe.ingredients) {
      const key = ing.product || ing.name || '';
      if (key) map[key] = findMatchedProduct(ing, productList);
    }
    return map;
  }, [recipe?.ingredients, productList]);

  // Augment ingredients with their localized display name so the per-instruction
  // matcher can also match against the localized text shown in the cooking steps.
  // Cooking steps are baked in the language the recipe was generated in, which may
  // differ from the current UI language. So we attach EVERY localized name from the
  // product's lang map (not just the current language) as match candidates.
  const augmentedIngredients = useMemo(() => {
    if (!recipe?.ingredients) return [];
    return recipe.ingredients.map((ing) => {
      const mp = ingredientProducts[ing.product || ing.name || ''];
      const localizedNames = [];
      if (mp) {
        const single = getLocalizedProductName(mp);
        if (single) localizedNames.push(single);
        let langMap = mp.lang;
        if (typeof langMap === 'string') {
          try { langMap = JSON.parse(langMap); } catch { langMap = null; }
        }
        if (langMap && typeof langMap === 'object') {
          for (const val of Object.values(langMap)) {
            if (typeof val === 'string' && val.trim()) localizedNames.push(val);
          }
        }
        if (mp.displayName) localizedNames.push(mp.displayName);
        if (mp.productName) localizedNames.push(mp.productName);
        if (Array.isArray(mp.allOriginals)) {
          for (const o of mp.allOriginals) if (o) localizedNames.push(o);
        }
        if (mp.originalName) localizedNames.push(mp.originalName);
      }
      return {
        ...ing,
        localizedName: localizedNames[0] || null,
        localizedNames: Array.from(new Set(localizedNames)),
      };
    });
  }, [recipe?.ingredients, ingredientProducts]);

  // Localized measure hints emitted by the LLM, keyed by English ingredient name
  // (from `ingredient_storage[].measure_hint`). Helps the user eyeball weights
  // and volumes. Older saved recipes have no hints here and fall back to a
  // client-side household-measure conversion at render time.
  const measureHintMap = useMemo(() => {
    const map = {};
    let instr = recipe?.instructions;
    if (typeof instr === 'string') {
      try { instr = JSON.parse(instr); } catch { instr = null; }
    }
    const storage = instr && instr.ingredient_storage;
    if (Array.isArray(storage)) {
      for (const s of storage) {
        if (s && typeof s.name === 'string' && typeof s.measure_hint === 'string' && s.measure_hint.trim()) {
          map[s.name.toLowerCase().trim()] = s.measure_hint.trim();
        }
      }
    }
    return map;
  }, [recipe?.instructions]);

  // Look up the LLM measure hint for an English ingredient name (with a loose
  // substring fallback for minor name drift).
  const resolveLlmHint = useCallback((engName) => {
    if (!engName) return null;
    const key = String(engName).toLowerCase().trim();
    if (!key) return null;
    if (measureHintMap[key]) return measureHintMap[key];
    for (const k of Object.keys(measureHintMap)) {
      if (key.includes(k) || k.includes(key)) return measureHintMap[k];
    }
    return null;
  }, [measureHintMap]);


  // Current global position in the flattened instruction list.
  const flatIndex = useMemo(
    () => flat.findIndex((f) => f.stepIdx === stepIdx && f.instrIdx === instrIdx),
    [flat, stepIdx, instrIdx]
  );

  const goToFlat = useCallback(
    (ti, dir) => {
      if (ti < 0 || ti >= flat.length) return;
      setDirection(dir);
      setStepIdx(flat[ti].stepIdx);
      setInstrIdx(flat[ti].instrIdx);
    },
    [flat]
  );

  const goToStep = useCallback(
    (i) => {
      const ti = flat.findIndex((f) => f.stepIdx === i);
      if (ti < 0) return;
      goToFlat(ti, i >= stepIdx ? 'next' : 'prev');
    },
    [flat, goToFlat, stepIdx]
  );

  const handleComplete = useCallback(async () => {
    if (recipeId) {
      await completeCooking(recipeId);
      if (storageKey) localStorage.removeItem(storageKey);
    }
    navigate('/mymeals');
  }, [recipeId, storageKey, completeCooking, navigate]);

  const handleNext = useCallback(() => {
    if (flatIndex >= flat.length - 1) {
      handleComplete();
    } else {
      goToFlat(flatIndex + 1, 'next');
    }
  }, [flatIndex, flat.length, goToFlat, handleComplete]);

  const handlePrev = useCallback(() => {
    if (flatIndex > 0) goToFlat(flatIndex - 1, 'prev');
  }, [flatIndex, goToFlat]);

  // Vertical swipe: up → next, down → previous.
  const touchStartY = useRef(null);
  const onTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e) => {
    if (touchStartY.current == null) return;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    touchStartY.current = null;
    const THRESHOLD = 50;
    if (deltaY <= -THRESHOLD) handleNext();
    else if (deltaY >= THRESHOLD) handlePrev();
  };

  const handleBack = () => navigate(`/mymeals/recipe/${index}`);

  if (!recipe || steps.length === 0) {
    return (
      <div className="min-h-screen bg-white">
        <header className="flex items-center h-[56px] px-3 border-b border-[#F0EFED]">
          <button onClick={() => navigate('/mymeals')} className="w-9 h-9 flex items-center justify-center" aria-label="Back">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="#0D0C07" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span className="ml-1 text-[15px] font-semibold text-[#0D0C07]">{tRecipes('cookingMode.title', 'Cooking Mode')}</span>
        </header>
        <div className="px-5 py-16 text-center text-[#65635D] text-[15px]">{tRecipes('drawer.noInstructionsAvailable', 'No cooking instructions available')}</div>
      </div>
    );
  }

  const currentStep = steps[stepIdx];
  const activeText = flat[flatIndex] ? flat[flatIndex].text : '';
  const prevText = flatIndex > 0 ? flat[flatIndex - 1].text : null;
  const nextText = flatIndex >= 0 && flatIndex < flat.length - 1 ? flat[flatIndex + 1].text : null;
  const isFirstOverall = flatIndex <= 0;
  const isFinal = flatIndex === flat.length - 1;

  // Per-step English ingredient names emitted by the LLM (when present) act as a
  // whitelist so only this step's ingredients can match the active line.
  const activeStepIngredientNames = Array.isArray(currentStep?.raw?.ingredients)
    ? currentStep.raw.ingredients.filter((n) => typeof n === 'string' && n.trim())
    : null;
  const activeIngredients = getStepIngredients(activeText, augmentedIngredients, activeStepIngredientNames);
  const activeAnimation = direction === 'next' ? 'cookActiveFromNext' : 'cookActiveFromPrev';
  const prevPreviewAnimation = direction === 'next' ? 'cookPrevFromActive' : 'cookPrevPeek';
  const nextPreviewAnimation = direction === 'prev' ? 'cookNextFromActive' : 'cookNextPeek';

  return (
    <div ref={pageRef} className="h-[100dvh] bg-[#F3F3F2] items-center flex flex-col w-full mx-auto overflow-hidden overscroll-none touch-none">
      {/* Custom header */}
      <header  className="h-[56px] px-3 border-b border-[#F0EFED] bg-white z-10 items-center flex-shrink-0 flex justify-center w-full">
        <div className="flex max-w-5xl flex-shrink-0 flex items-center justify-start w-full">
        <button onClick={handleBack} className="w-9 h-9 flex items-center justify-center" aria-label="Back">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="#0D0C07" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <span className="ml-1 text-[15px] font-semibold text-[#0D0C07]">{tRecipes('cookingMode.title', 'Cooking Mode')}</span>
        </div>
      </header>

      {/* Block 1: step progress bars + step title */}
      <div className="flex flex-col px-5 pt-3 pb-0 items-center justify-center w-full">
        <div className="max-w-5xl flex items-center gap-2 w-full" role="list" aria-label={tRecipes('cookingMode.stepCounter', 'Recipe steps')}>
          {steps.map((s, i) => {
            const isCompleted = i < stepIdx;
            const isActive = i === stepIdx;
            return (
              <button
                key={i}
                type="button"
                onClick={() => goToStep(i)}
                aria-label={`Step ${i + 1}`}
                aria-current={isActive ? 'step' : undefined}
                className="flex-1 h-8 flex items-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0D0C07]/20"
                role="listitem"
              >
                <span
                  className={`block w-full h-[5px] rounded-full transition-colors ${
                    isCompleted ? 'bg-[#009900]' : isActive ? 'bg-[#FFCB00]' : 'bg-[#E2E1DF]'
                  }`}
                />
              </button>
            );
          })}
        </div>
        <h2 className="text-[18px] font-bold text-[#0D0C07] leading-[26px] mt-3 w-full max-w-5xl">
          {currentStep.stepTitle}
        </h2>
      </div>

      {/* Swipeable instruction content — stretched full height: prev top, active middle, next bottom */}
      <div
        className="flex-1 max-w-5xl w-full min-h-0 flex flex-col px-5 pb-[104px] overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* Block 2: previous instruction (top) */}
        <div className="flex-shrink-0 pt-2 min-h-[40px] flex items-start">
          {prevText && (
            <button
              key={`prev-${flatIndex}`}
              onClick={handlePrev}
              style={{
                animation: `${prevPreviewAnimation} 520ms cubic-bezier(0.22, 1, 0.36, 1) both`,
                transformOrigin: 'left top',
                willChange: 'transform, opacity',
              }}
              className="cook-motion block w-full text-left"
            >
              <p className="text-[12px] text-[#A8A6A0] leading-[20px] line-clamp-2">{prevText}</p>
            </button>
          )}
        </div>

        {/* Block 3: active instruction + its ingredients (middle, animated on change) */}
        <div
          key={flatIndex}
          className="flex-1 min-h-0 overflow-y-auto py-4"
        >
          <div className="min-h-full flex flex-col justify-center">
            <p
              style={{
                animation: `${activeAnimation} 560ms cubic-bezier(0.22, 1, 1, 1) both`,
                transformOrigin: 'left center',
                willChange: 'transform, opacity',
              }}
              className="cook-motion text-[18px] font-medium text-[#0D0C07] leading-[32px] mb-5"
            >
              {activeText}
            </p>

            {activeIngredients.length > 0 && (
              <div className="space-y-2">
                {activeIngredients.map((ingredient, ii) => {
                  const matchedProduct = ingredientProducts[ingredient.product || ingredient.name || ''];
                  const productImage = matchedProduct ? getProductImage(matchedProduct) : '/assets/products/no-image.jpg';
                  const displayName = (matchedProduct ? getLocalizedProductName(matchedProduct) : null) || ingredient.product || ingredient.name;
                  const qtyStr = ingredient.quantity || ingredient.qty;
                  // Show a measure hint only for weight/volume quantities. Prefer
                  // the LLM's localized hint; fall back to a client household measure.
                  let measureHint = null;
                  if (isWeightOrVolumeQty(qtyStr)) {
                    measureHint = resolveLlmHint(ingredient.name);
                    if (!measureHint) {
                      const fb = getMeasureHint(qtyStr, ingredient.name || ingredient.product);
                      measureHint = fb ? translateQty(fb) : null;
                    }
                  }
                  return (
                    <div
                      key={ii}
                      style={{
                        animation: `cookProductIn 420ms cubic-bezier(0.22, 1, 0.36, 1) ${260 + ii * 80}ms both`,
                        willChange: 'transform, opacity',
                      }}
                      className="cook-motion border-[#fff] border-[1px] flex items-center gap-3 p-1 rounded-[12px] bg-[#F8F8F7]"
                    >
                      <div className="flex-shrink-0 w-12 h-12 rounded-[8px] overflow-hidden bg-gray-100">
                        <img
                          src={productImage}
                          alt={displayName}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = '/assets/products/no-image.jpg';
                          }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-[14px] mb-0.5 font-medium text-[#0D0C07] leading-[20px] truncate">{displayName}</h4>
                        <p className="text-[13.2px] text-[#65635D] leading-[20px]">
                          {translateQty(qtyStr)}
                          {measureHint && <span className="text-[#A8A6A0]"> · {measureHint}</span>}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Block 4: swipe tip (first instruction only) + next instruction (bottom) */}
        <div className="flex-shrink-0 min-h-[48px] flex flex-col justify-end">
          {isFirstOverall && (
            <div className="md:hidden flex flex-col items-center justify-center mb-3 text-[#A8A6A0] animate-bounce">
              <span className="text-[13px] font-medium mb-0.5">{tRecipes('cookingMode.swipe', 'Swipe')}</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M6 9L12 15L18 9" stroke="#A8A6A0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}
          {nextText && (
            <button
              key={`next-${flatIndex}`}
              onClick={handleNext}
              style={{
                animation: `${nextPreviewAnimation} 520ms cubic-bezier(0.22, 1, 0.36, 1) 120ms both`,
                transformOrigin: 'left bottom',
                willChange: 'transform, opacity',
              }}
              className="cook-motion block w-full text-left"
            >
              <p className="text-[14px] text-[#72726E] leading-[24px] line-clamp-2">{nextText}</p>
            </button>
          )}
        </div>
      </div>

      {/* Bottom action */}
      <div
        className="
          fixed bottom-[16px] left-[20px] right-[20px]
          py-2 px-2
          flex justify-center
          z-50
          max-w-5xl
          mx-auto
          rounded-[96px]
          bg-[linear-gradient(135deg,_rgba(243,243,242,0.8)_0%,_rgba(243,243,242,0.9)_100%)]
          backdrop-blur-[64px]
          border-[1px] border-[#fff]
          shadow-[0px_0px_4px_0px_rgba(13,12,7,0.1),_1px_-2px_16px_0px_rgba(13,12,7,0.1)]
        "
      >
        <div className="w-full max-w-5xl">
        <button
          onClick={handleNext}
          className="
            relative
            inline-flex justify-center items-center gap-1
            h-[48px] px-[24px] py-[14px]
            rounded-[32px]
            text-[#0D0C07] text-center
            text-[14.2px] font-semibold leading-[28px]
            transition-colors
            w-full
            bg-[#FFCB00]
            shadow-[0_2px_16px_0_rgba(0,0,0,0.08)_inset,_0_0_32px_0_rgba(255,255,255,0.35)_inset,_0_0px_0px_1px_rgba(230,183,0,0.55)]
            md:hover:bg-[#FFD700]
          "
        >
          {isFinal
            ? tMeal('recipeCard.completeCooking', 'Complete Cooking')
            : tRecipes('cookingMode.nextStepAction', 'Next step')}
        </button>
        </div>
      </div>
    </div>
  );
};

export default CookingModePage;
